import { useState } from "react";
import type { ReactNode } from "react";
import { History } from "lucide-react";
import type { AuditEventView, OrgMemberView } from "../../api/teams.js";
import { useAudit, useOrgMembers } from "../../data/teams.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { AUDIT_LABEL, describeTeamError, groupAuditEvents } from "../../lib/teams.js";
import { Switch } from "../../ui/forms.js";
import { Alert, Avatar, Card, CardHeader, Skeleton } from "../../ui/primitives.js";

function actorLabel(actorId: string | null, members: ReadonlyArray<OrgMemberView> | undefined): string {
  if (actorId === null) {
    return "System";
  }
  const member = members?.find((m: OrgMemberView): boolean => m.userId === actorId);
  return member?.email ?? "Ehemaliges Mitglied";
}

export function TeamAuditTab(props: { readonly orgId: string }): ReactNode {
  const audit = useAudit(props.orgId);
  const members = useOrgMembers(props.orgId);
  const [showReads, setShowReads] = useState(false);
  const groups = groupAuditEvents<AuditEventView>(audit.data ?? [], showReads);
  const now = Date.now();

  return (
    <Card className="overflow-hidden">
      <CardHeader title="Protokoll" description="Wer hat was wann getan. Ohne Geheimnisse, Adressen werden nur gehasht gespeichert." icon={History} />
      <div className="flex flex-col gap-5 p-5">
        <div className="rounded-lg border border-line bg-ink px-4 py-2.5">
          <Switch
            label="Lesezugriffe anzeigen"
            description="Jedes Öffnen einer Sammlung oder eines Eintrags wird protokolliert."
            checked={showReads}
            onChange={setShowReads}
          />
        </div>
        {audit.isPending ? <Skeleton className="h-40" /> : null}
        {audit.isError ? <Alert tone="danger">{describeTeamError(audit.error)}</Alert> : null}
        {audit.data !== undefined && groups.length === 0 ? (
          <p className="rounded-lg border border-dashed border-line-strong px-5 py-6 text-center text-sm text-mist">Noch keine Ereignisse.</p>
        ) : null}
        {groups.length > 0 ? (
          <ol className="relative ml-3.5 border-l border-line">
            {groups.map((group) => {
              const event = group.first;
              const actor = actorLabel(event.actorId, members.data);
              return (
                <li key={event.id} className="relative pb-5 pl-7 last:pb-0">
                  <span className="absolute -left-3.5 top-0 rounded-md ring-4 ring-ink-2" aria-hidden="true">
                    <Avatar label={actor} size="sm" />
                  </span>
                  <p className="text-sm font-medium text-foam">
                    {AUDIT_LABEL[event.action] ?? event.action}
                    {group.count > 1 ? <span className="ml-2 rounded border border-flow/25 bg-flow/10 px-1.5 py-px text-xs font-medium text-flow">×{group.count}</span> : null}
                  </p>
                  <p className="mt-0.5 truncate text-[0.8125rem] text-mist-2">
                    <span className="text-mist">{actor}</span>
                    {" · "}
                    <time dateTime={new Date(event.createdAt).toISOString()} title={formatDateTime(event.createdAt)}>
                      {formatRelativeTime(event.createdAt, now)}
                    </time>
                  </p>
                </li>
              );
            })}
          </ol>
        ) : null}
      </div>
    </Card>
  );
}
